/* ------------------------------------------------------------------
 *  Footer — Édition "Éditorial Academy"
 *  Marine profond, typo Fraunces, hairlines, accent doré, newsletter,
 *  wordmark XXL en signature.
 * ------------------------------------------------------------------ */
import { useState } from "react"
import { Link } from "react-router-dom"
import {
  ArrowUpRight,
  Mail,
  MapPin,
  Phone,
  Facebook,
  Linkedin,
  Instagram,
  Youtube,
} from "lucide-react"
import { useTranslation } from "../i18n"
import { useNewsletterSubscription } from "../hooks/useNewsletter"

function Eyebrow({ children, className = "" }) {
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-[0.28em] font-medium ${className}`}
    >
      {children}
    </span>
  )
}

export default function Footer() {
  const { tObj } = useTranslation()
  const currentYear = new Date().getFullYear()
  const [email, setEmail] = useState("")
  const { subscribe, loading } = useNewsletterSubscription()

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!email.trim()) return
    try {
      await subscribe({ email: email.trim(), source: "footer" })
      setEmail("")
    } catch {
      /* toast géré par le hook */
    }
  }

  return (
    <footer className="relative text-white/90 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/banner.jpg"
          alt="AY Solutions Academy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#203a8a]/85" />
      </div>
      
      {/* Texture pointillée */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none z-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />

      {/* ============================================================
       *  Grille principale — 4 colonnes éditoriales
       * ============================================================ */}
      <div className="border-t border-white/10 relative">
        <div className="edito-container py-12 sm:py-16 relative">
          <div className="grid grid-cols-12 gap-8">
            {/* Brand */}
            <div className="col-span-12 md:col-span-12 lg:col-span-4">
              <a
                href="https://ay-solutions.org/"
                className="inline-flex items-center gap-3 mb-5"
                title="AY SOLUTIONS"
              >
                <img
                  src="/images/logo.png"
                  alt="AY SOLUTIONS"
                  className="h-16 w-auto object-contain bg-white p-1"
                />
                <div className="leading-tight">
                  <Eyebrow className="!text-white/60">AY Solutions</Eyebrow>
                  <p className="font-display text-2xl text-white mt-0.5">
                    Academy
                  </p>
                </div>
              </a>
              <p className="text-sm text-white/70 leading-relaxed max-w-sm">
                {tObj.footer.description()}
              </p>

              {/* Réseaux sociaux */}
              <div className="mt-6 flex items-center gap-3">
                {[
                  { Icon: Facebook,  href: "https://www.facebook.com/profile.php?id=100064013980535", label: "Facebook" },
                  { Icon: Linkedin,  href: "https://www.linkedin.com/company/ay-solutions-centre-d-incubation-et-d-insertion-des-jeunes/posts/?feedView=all", label: "LinkedIn" },
                  { Icon: Instagram, href: "https://www.instagram.com/ay_solutions.academy/?hl=fr", label: "Instagram" },
                  { Icon: Youtube,   href: "https://www.youtube.com/channel/UCtNDuaM1Smwt01YEZekhNdQ", label: "YouTube" },
                ].map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="w-10 h-10 border border-white/20 hover:border-[#E8C547] hover:text-[#E8C547] flex items-center justify-center transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="col-span-6 md:col-span-4 lg:col-span-2">
              <Eyebrow className="!text-[#E8C547]/80">Naviguer</Eyebrow>
              <ul className="mt-5 space-y-3 text-sm">
                {[
                  { to: "/",         label: "Accueil" },
                  { to: "/about",    label: "À propos" },
                  { to: "/courses",  label: "Cours" },
                  { to: "/webinars", label: "Webinaires" },
                  { to: "/faq",      label: "FAQ" },
                  { to: "/contact",  label: "Contact" },
                ].map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="group inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                    >
                      <span className="w-0 h-px bg-[#E8C547] transition-all duration-500 group-hover:w-4" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="col-span-6 md:col-span-4 lg:col-span-2">
              <Eyebrow className="!text-[#E8C547]/80">Contact</Eyebrow>
              <ul className="mt-5 space-y-4 text-sm text-white/70">
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-0.5 text-[#E8C547] flex-shrink-0" />
                  <span>Abidjan, Côte d'Ivoire</span>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 mt-0.5 text-[#E8C547] flex-shrink-0" />
                  <span>
                    (+225) 0596808080
                    <br />
                    (+225) 27 22 498 133
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-0.5 text-[#E8C547] flex-shrink-0" />
                  <a
                    href="mailto:contact@ay-solutions.org"
                    className="hover:text-white transition-colors"
                  >
                    contact@ay-solutions.org
                  </a>
                </li>
              </ul>
            </div>

            {/* Newsletter */}
            <div className="col-span-12 md:col-span-4 lg:col-span-4">
              <Eyebrow className="!text-[#E8C547]/80">Newsletter</Eyebrow>
              <p className="font-display text-xl text-white leading-tight mt-3 mb-5">
                Recevez chaque mois
                <span className="italic text-white/70"> nos meilleures lectures.</span>
              </p>
              <form onSubmit={onSubmit} className="flex border border-white/20 focus-within:border-[#E8C547] transition-colors">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="flex-1 bg-transparent text-white placeholder:text-white/40 px-4 py-3 text-sm focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 bg-[#E8C547] text-[#203a8a] hover:bg-[#d4af3a] disabled:opacity-60 transition-colors text-xs uppercase tracking-[0.22em] font-medium"
                >
                  {loading ? "..." : "OK"}
                </button>
              </form>
              <p className="text-xs text-white/50 mt-3">
                Pas de spam. Désabonnement en un clic.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
       *  Wordmark XXL signature
       * ============================================================ */}
      <div className="border-t border-white/10 relative overflow-hidden">
        <div className="edito-container py-8 relative">
          <p className="font-display text-center text-white/[0.06] text-[clamp(4rem,16vw,14rem)] leading-none tracking-tight select-none">
            ACADEMY
          </p>
        </div>
      </div>

      {/* ============================================================
       *  Bottom bar
       * ============================================================ */}
      <div className="border-t border-white/10 relative">
        <div className="edito-container py-5 relative">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-white/60">
            <p>{tObj.footer.copyright({ year: currentYear })}</p>
            <div className="flex items-center gap-5">
              <Link to="/privacy" className="hover:text-white transition-colors">
                Confidentialité
              </Link>
              <span className="w-px h-3 bg-white/20" />
              <Link to="/terms" className="hover:text-white transition-colors">
                Conditions
              </Link>
              <span className="w-px h-3 bg-white/20" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-white/40">
                Made with ❤ in Africa
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
